import React from "react";
import PolicyLayout from "../../../components/PolicyLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function AiCollagePrivacy() {
  return (
    <PolicyLayout
      title="privacy policy"
      appName="Smart Collage Ai"
      date="DECEMBER 2025"
    >
      <div className={s.title}>1) Introduction and Scope</div>
      <p>
        This Privacy Policy explains how “AI Collage” (the “App”) collects,
        uses, stores, and discloses information about users. The App lets you
        import photos from your gallery, choose a grid or freestyle layout, make
        aesthetic edits (backgrounds, borders, cropping/zoom), and export or
        share the final collage. Core editing runs locally on your device
        without registration or our own backend. Certain optional image tools
        may use trusted third-party services to process selected images on
        request. This Policy covers onboarding, photo access, local editing and
        storage, optional third-party processing, exports/sharing,
        subscriptions, analytics and crash diagnostics, and support. By using
        the App, you agree to these practices. If you do not agree, uninstall
        the App.
      </p>

      <div className={s.title}>2) Controller and Contact Information</div>
      <p>
        The data controller that determines purposes and means of processing is
        Aleksandra Rakitina. For privacy questions or to exercise rights such as
        access, deletion, correction, restriction, objection, or portability,
        contact alexwadek@gmail.com. We verify identity before fulfilling
        requests, typically by confirming control of your contact email or by a
        minimal verification step. We retain privacy correspondence as allowed
        by law for compliance and security.
      </p>

      <div className={s.title}>
        3) Key Principles (Local-First, Minimization, User Control)
      </div>
      <p>
        Local-first: core collage composition and settings are processed and
        stored on your device; we do not run a cloud backend for your projects.
        Minimization: we collect only what is necessary to deliver
        subscriptions, reliability analytics, and crash diagnostics; we do not
        sell Personal Data. User control: you choose which photos to import,
        which tools to apply, where to export/share, and when to delete
        projects. Optional online tools are clearly labeled and only upload
        content needed for that specific operation.
      </p>

      <div className={s.title}>4) Definitions</div>
      <p>
        “Personal Data” means information relating to an identified or
        identifiable person. “Project Content” refers to photos you import and
        the collages you create. “Local Metadata” includes project IDs,
        thumbnails/previews, layout choices, background/border settings,
        timestamps, and export status stored on-device. “Processing” includes
        collection, storage, transformation, transmission, export, and deletion.
        “Service Providers/Processors” are third parties acting on our
        instructions (subscriptions, analytics/crash, and optional image APIs).
      </p>

      <div className={s.title}>5) Categories of Data We Process</div>
      <p>
        Depending on your choices, we may process: (a) Project Content you
        import and the resulting collages (stored locally by default); (b) Local
        Metadata (project list, template selections, background/border
        parameters, preview paths); (c) Subscription/entitlement metadata from
        RevenueCat (product IDs, trial eligibility,
        renewal/expiration/cancellation) to grant premium access; (d) Analytics
        and crash diagnostics via Firebase (device/OS/app versions, anonymized
        error codes, and counters such as photo_imported, template_selected,
        collage_saved, paywall_opened, trial_started, purchase_success); (e)
        Optional vendor payloads when you use online tools like background
        removal or special filters (only the images or derived data needed to
        perform that operation are sent to the chosen provider). We do not
        receive payment card numbers.
      </p>

      <div className={s.title}>6) Sources of Data and Voluntary Provision</div>
      <p>
        Most information originates from you (photos you pick, edits you make).
        Technical diagnostics arise automatically to maintain stability. Online
        tools are optional; choosing them causes the relevant image to be
        transmitted to a provider strictly to perform the requested effect. You
        can use the App in a fully local mode without these tools. Some features
        require permissions: gallery access for photos; network for
        subscriptions and optional APIs.
      </p>

      <div className={s.title}>7) Photo Library Access and Local Editing</div>
      <p>
        The App requests permission to access your device gallery so you can
        select 1–9 photos. We only read the items you choose; we do not scan
        your entire library. Editing (layouts, backgrounds, borders,
        zoom/rotate) occurs locally. Thumbnails and project files are stored in
        the app sandbox. You can replace images, undo/redo, and delete projects
        at any time. Uninstalling the App removes sandboxed content from your
        device.
      </p>

      <div className={s.title}>8) Optional Online Image Tools (Premium Features)</div>
      <p>
        Some premium features may rely on third-party image APIs (for example:
        PixLab for compositing/templates, Remove.bg/PhotoRoom for background
        removal, Imgix for transformations). If you activate such a feature, the
        App sends only the necessary image data over HTTPS to the selected
        provider, which returns a processed result to your device. We do not add
        advertising identifiers to these requests. Providers are contractually
        required to secure data and act on our instructions. If a provider
        temporarily caches data for reliability/abuse prevention, retention
        follows that provider’s policy. You can avoid online processing by not
        enabling these features.
      </p>

      <div className={s.title}>9) “AI Collage” Mode (Coming Soon)</div>
      <p>
        The “AI Collage” button may be shown as disabled/coming soon in MVP. If
        enabled in a future release and powered by a server model (e.g.,
        Replicate), we will clearly disclose the provider and what data is sent
        (selected images and minimal parameters to compute a layout). Until that
        feature is live, your editing remains local unless you initiate the
        other optional online tools described above.
      </p>

      <div className={s.title}>10) Exports, Sharing, and Destinations</div>
      <p>
        When you save or share a collage (e.g., JPG/PNG at 1080×1080), copies
        are written to locations or apps you choose (Photos, Files, Instagram,
        Pinterest, WhatsApp). These destinations have their own privacy
        practices we do not control. Deleting a project inside the App does not
        remove exported copies elsewhere. Consider destination visibility and
        access controls before sharing.
      </p>

      <div className={s.title}>11) Local Storage (Hive), Previews, and Deletion</div>
      <p>
        Project records (previews + file paths) and user settings (last chosen
        template, background color/gradient, border styles) are stored locally
        using Hive. You can clear individual projects from the in-app gallery or
        remove all App data via device settings. Deleting a record removes the
        local file and preview from the sandbox; external exports remain where
        you saved them.
      </p>

      <div className={s.title}>12) Free Tier Limits and Paywall</div>
      <p>
        The free tier may allow two saved collages and exclude logo/“Pro
        Template” sets. The paywall appears if you attempt to save a third
        collage or select a Pro Template. Premium unlocks unlimited saves and
        pro tools (including certain online effects) subject to fair-use limits
        designed to keep performance and costs reasonable.
      </p>

      <div className={s.title}>
        13) Subscriptions, Trials, Pricing, and Entitlements
      </div>
      <p>
        Subscriptions use platform billing via RevenueCat. The weekly plan
        icollage_premium_weekly is currently $6.99/week with a 3-day free
        trial. We receive subscription metadata (product IDs, trial eligibility,
        renewal outcomes, expiration/cancellation) to grant or revoke access.
        Manage or cancel in your device account settings; refunds and grace
        periods follow platform rules and applicable law. We do not receive card
        details.
      </p>

      <div className={s.title}>
        14) Analytics, Performance, and Crash Diagnostics
      </div>
      <p>
        We use Firebase Analytics and Crashlytics to understand reliability and
        feature adoption, not to build behavioral advertising profiles. Typical
        events: photo_imported, template_selected, collage_saved,
        paywall_opened, trial_started, purchase_success, plus anonymized error
        codes and device/OS/app versions. Where feasible, analytics are
        aggregated/pseudonymized and exclude your full-resolution photos or
        exported collages.
      </p>

      <div className={s.title}>15) Notifications (If Used)</div>
      <p>
        If enabled, the App may show local reminders or tips (e.g., “Finish your
        collage”). You can disable notifications in system settings and, where
        provided, in-app toggles. We avoid placing sensitive content in
        notification bodies. Uninstalling typically cancels local schedules.
      </p>

      <div className={s.title}>16) What We Do Not Collect or Sell</div>
      <p>
        We do not operate a cloud backend for your Project Content. We do not
        sell Personal Data. We do not require account registration for core use.
        We do not request precise geolocation or contacts. We do not embed
        hidden trackers in exported images.
      </p>

      <div className={s.title}>17) Security Measures</div>
      <p>
        We implement administrative, technical, and organizational safeguards
        appropriate to the risks: HTTPS for provider communications;
        app-sandboxed local storage; least-privilege access to operational
        tools; credential rotation; and separation of development and
        production. While no method is perfectly secure, our local-first design
        reduces exposure by keeping most editing and storage on-device.
      </p>

      <div className={s.title}>18) Data Retention and Deletion</div>
      <p>
        Local projects/settings persist until you delete them or uninstall the
        App. Provider-side subscription records and limited analytics/crash logs
        are retained for operational windows and legal/accounting purposes,
        typically in pseudonymized or aggregated form. If you email support, we
        retain correspondence as needed for issue resolution and compliance,
        then delete or archive per policy.
      </p>

      <div className={s.title}>19) International Data Transfers</div>
      <p>
        Optional online tools and subscription/analytics providers may process
        limited data in other jurisdictions. Where required, we rely on
        appropriate safeguards for cross-border transfers (contractual
        protections and platform mechanisms). Providers must implement security
        measures consistent with this Policy and applicable law.
      </p>

      <div className={s.title}>20) Your Rights and Choices</div>
      <p>
        Subject to law, you may have rights to access, correct, delete, or port
        Personal Data; to restrict or object to certain processing; and to
        withdraw consent where processing relies on consent. In-app you can
        delete projects, clear previews, change templates/backgrounds, and avoid
        online tools. Subscription management (including cancellation/refunds)
        occurs in platform settings. To exercise other rights, email
        alexwadek@gmail.com; we will verify identity and respond within a
        reasonable time.
      </p>

      <div className={s.title}>
        21) Do Not Track, Identifiers, and SDK Disclosures
      </div>
      <p>
        As a native app, we do not use browser cookies. SDKs may store
        identifiers for subscriptions, analytics, and crash reporting; these
        function similarly to cookies by enabling sessions and measurement. The
        App does not rely on advertising IDs for targeted ads. Browser “Do Not
        Track” signals generally do not apply to native apps.
      </p>

      <div className={s.title}>22) Children’s Privacy</div>
      <p>
        The App is not directed to children, and we do not knowingly collect
        Personal Data from users under the age where parental consent is
        required by law. If you believe a minor used the App without appropriate
        authorization, contact alexwadek@gmail.com and we will remove
        information and terminate access where applicable.
      </p>

      <div className={s.title}>
        23) Third-Party Links and External Destinations
      </div>
      <p>
        If you open links to documentation, storefronts, support pages, or
        platform settings, you leave the App environment and the destination’s
        privacy practices apply. Exported files shared to other apps/services
        are governed by those services’ policies.
      </p>

      <div className={s.title}>
        24) Review Transparency and Platform Compliance
      </div>
      <p>
        We disclose: (a) core editing and storage are local by default; (b)
        online tools (if used) send only necessary image data to a chosen
        provider for processing; (c) subscriptions are handled via platform
        billing/RevenueCat; (d) analytics/crash logs focus on reliability; and
        (e) a paywall appears after free-tier thresholds or when selecting Pro
        Templates. Any future feature that changes data flows (e.g., server-side
        AI layouts) will be disabled by default and accompanied by in-app
        disclosures and an updated Policy.
      </p>

      <div className={s.title}>25) Changes to This Policy</div>
      <p>
        We may update this Policy to reflect operational, legal, or regulatory
        changes. The “Last Updated” date will change when updates occur.
        Material changes affecting your rights or our processing will be
        highlighted in-app; where required, we will request consent. Continued
        use after changes take effect signifies acceptance.
      </p>

      <div className={s.title}>
        26) How to Submit Privacy Requests and Verification Steps
      </div>
      <p>
        To exercise your rights, email alexwadek@gmail.com and specify the
        request type (access, deletion, correction, restriction, objection,
        portability). Include helpful context such as device model and app
        version. We verify identity to prevent unauthorized access or deletion
        and may request limited additional details or a confirmation code. Where
        feasible, responses are provided in a portable, machine-readable format.
        We may decline or limit requests when permitted by law (for example, if
        fulfilling a request would compromise security, interfere with legal
        obligations, or infringe others’ privacy).
      </p>
    </PolicyLayout>
  );
}
